import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { assertSafePayload, deepSanitizePayload } from './utils/inputValidation';
import { initGlobalInputSanitizer } from './utils/globalInputSanitizer';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Initialize global DOM visual validation and submit guards against XSS
initGlobalInputSanitizer();

const nativeFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  // Silent sanitization for URL query parameters
  if (typeof input === 'string' && input.includes('?')) {
    try {
      const [urlPath, queryString] = input.split('?');
      const searchParams = new URLSearchParams(queryString);
      const cleanParams = new URLSearchParams();
      for (const [key, val] of searchParams.entries()) {
        cleanParams.append(key, deepSanitizePayload(val));
      }
      input = `${urlPath}?${cleanParams.toString()}`;
    } catch {
      // Fallback
    }
  }

  // Silent sanitization for fetch body (objects, JSON strings, FormData, etc.)
  if (init && init.body) {
    init.body = deepSanitizePayload(init.body);
  }

  const response = await nativeFetch(input, init);

  // If 401 Unauthorized received on an internal API call, attempt silent token refresh and retry
  if (response.status === 401 && !init._retry) {
    const urlStr = typeof input === 'string' ? input : (input && input.url ? input.url : '');
    const isExcluded =
      urlStr.includes('/login') ||
      urlStr.includes('/refresh-token') ||
      urlStr.includes('/refresh') ||
      urlStr.includes('/forgot-password') ||
      urlStr.includes('/reset-password') ||
      urlStr.includes('/verify-reset-token');

    if (!isExcluded) {
      try {
        const { authApi } = await import('./api/auth.api');
        await authApi.refreshToken();
        const retryInit = { ...init, _retry: true };
        return await nativeFetch(input, retryInit);
      } catch (e) {
        // Refresh failed, return original 401 response
      }
    }
  }

  return response;
};

axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  if (config.data) {
    config.data = deepSanitizePayload(config.data);
  }
  if (config.params) {
    config.params = deepSanitizePayload(config.params);
  }
  if (typeof config.url === 'string' && config.url.includes('?')) {
    try {
      const [urlPath, queryString] = config.url.split('?');
      const searchParams = new URLSearchParams(queryString);
      const cleanParams = new URLSearchParams();
      for (const [key, val] of searchParams.entries()) {
        cleanParams.append(key, deepSanitizePayload(val));
      }
      config.url = `${urlPath}?${cleanParams.toString()}`;
    } catch {
      // Fallback
    }
  }
  return config;
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
reportWebVitals();
